import { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';

interface NavbarProps {
  onConsultationClick: () => void;
}

export default function Navbar({ onConsultationClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isDark, toggle } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Systems', href: '#solution' },
    { label: 'Industries', href: '#industries' },
    { label: 'Process', href: '#process' },
    { label: 'Contact', href: '#cta' },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/80 dark:bg-[#0B0B0C]/85 backdrop-blur-xl border-b border-[#D2D2D7]/60 dark:border-[#252528]/80'
          : 'bg-transparent backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[80px]">
        {/* Logo */}
        <a href="/" className="flex items-center cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/8f079269-9846-4254-b539-3b8eb1ff252a.png"
            alt="Auditore AI Logo"
            className="w-[120px] h-[120px] object-contain"
            style={{ filter: isDark ? 'drop-shadow(0 0 2px rgba(201,168,64,0.2))' : 'none' }}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="text-[#6E6E73] dark:text-[#96969E] hover:text-[#1D1D1F] dark:hover:text-[#F2F0EC] text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap tracking-wide"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Right controls */}
        <div className="hidden md:flex items-center gap-4">
          {/* Day/Night Toggle */}
          <button
            onClick={toggle}
            className={`relative w-14 h-7 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${
              isDark ? 'bg-[#252528]' : 'bg-[#D2D2D7]'
            }`}
            aria-label="Toggle theme"
          >
            <span
              className={`absolute top-1 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark
                  ? 'translate-x-7 bg-[#C9A840]'
                  : 'translate-x-1 bg-white shadow-sm'
              }`}
            >
              <i className={`text-[10px] ${isDark ? 'ri-moon-fill text-[#0B0B0C]' : 'ri-sun-fill text-[#C9A840]'}`}></i>
            </span>
          </button>

          {/* CTA */}
          <button
            onClick={onConsultationClick}
            className="whitespace-nowrap px-6 py-2.5 border border-[#C9A840] text-[#C9A840] text-sm tracking-wider font-medium hover:bg-[#C9A840] hover:text-[#0B0B0C] transition-all duration-300 cursor-pointer"
            style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            Consultation
          </button>
        </div>

        {/* Mobile controls */}
        <div className="md:hidden flex items-center gap-3">
          <button onClick={toggle} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className={`text-lg ${isDark ? 'ri-moon-line text-[#C9A840]' : 'ri-sun-line text-[#C9A840]'}`}></i>
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center text-[#1D1D1F] dark:text-[#F2F0EC] cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 dark:bg-[#0B0B0C]/95 backdrop-blur-xl border-t border-[#D2D2D7] dark:border-[#252528] px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="text-[#6E6E73] dark:text-[#96969E] hover:text-[#1D1D1F] dark:hover:text-[#F2F0EC] text-sm font-medium text-left cursor-pointer whitespace-nowrap tracking-wide"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => { onConsultationClick(); setMobileOpen(false); }}
            className="whitespace-nowrap px-6 py-3 border border-[#C9A840] text-[#C9A840] text-sm font-medium text-center cursor-pointer"
            style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            Request Consultation
          </button>
        </div>
      )}
    </header>
  );
}
